// Per-city data for Indiana location landing pages.
// Each entry powers a templated page at /locations/{slug}.
// Keep zip codes, neighborhoods, and nearby landmarks accurate.

export const LOCATIONS = [
  {
    slug: "columbus",
    name: "Columbus",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 50,000",
    distanceFromHQ: "Headquartered here — same-day service across the city",
    zipCodes: ["47201", "47202", "47203"],
    neighborhoods: [
      "Downtown Columbus",
      "Fair Oaks Mall area",
      "Brown Ridge",
      "Tipton Park",
      "Northbrook",
      "Country Club Estates",
      "Petersville",
    ],
    landmarks: [
      "Mill Race Park",
      "Columbus Commons",
      "Cummins Global Headquarters",
      "The Commons",
      "Columbus Regional Hospital",
    ],
    intro:
      "Columbus is our hometown — the original garage where Swift Move and Clean loaded its first truck in 2019. We know every back-street shortcut between downtown and the Fair Oaks Mall corridor, which homes on the north side of town have tight garage turns, and how long it actually takes to get from 25th Street to I-65 at 4:45 on a Friday. That local fluency is the difference between a move that wraps by lunch and a move that stretches into the evening.",
    highlights: [
      "Same-day scheduling available on weekdays",
      "Zero travel fees within Bartholomew County",
      "Most popular for: 3-bedroom residential moves + move-out cleans",
      "Crew typically dispatches from our 25th Street facility",
    ],
    localNote:
      "Columbus has a distinctive architectural identity (six National Historic Landmark buildings in a city this size is rare), and older downtown homes often have tight stairwells and original plaster walls. We send a smaller, experienced crew to heritage properties with extra wall padding and furniture pads rated for plaster contact.",
    testimonial: {
      name: "Rachel M.",
      neighborhood: "Brown Ridge, Columbus",
      text:
        "We've used Swift for two moves and a quarterly cleaning. They know this town cold — our last move to the east side took 45 minutes less than the quote because the crew knew to avoid the 46 construction. That kind of local knowledge saves real money.",
    },
    faq: [
      {
        q: "Do you charge a travel fee within Columbus?",
        a: "No — all locations within Bartholomew County are included in your hourly rate. There are no drive-time surcharges.",
      },
      {
        q: "Can you accommodate a same-day move?",
        a: "Often, yes — especially on weekdays. Call us by 10am and we can usually deploy a 2-person crew the same afternoon depending on crew availability.",
      },
      {
        q: "Do you service commercial moves downtown?",
        a: "Yes. We regularly handle office relocations within downtown Columbus and provide COI documents to any building manager who needs one.",
      },
    ],
  },
  {
    slug: "indianapolis",
    name: "Indianapolis",
    state: "Indiana",
    stateAbbr: "IN",
    population: "Over 880,000",
    distanceFromHQ: "50 miles north on I-65 — standard service area",
    zipCodes: [
      "46201", "46202", "46203", "46204", "46205", "46208", "46214", "46220", "46226", "46227", "46229", "46234", "46250", "46254", "46256", "46260",
    ],
    neighborhoods: [
      "Downtown (Mile Square)",
      "Broad Ripple",
      "Meridian-Kessler",
      "Fountain Square",
      "Irvington",
      "Castleton",
      "Geist",
      "Southport",
      "Speedway",
      "Mars Hill",
    ],
    landmarks: [
      "Monument Circle",
      "Eagle Creek Park",
      "Indiana Convention Center",
      "IU Health Methodist Hospital",
      "Indianapolis Motor Speedway",
    ],
    intro:
      "Indianapolis is our single busiest metro outside of Columbus. We run trucks into the city Monday through Saturday and have move-out cleaning crews based on the north side that can reach most of the 46xxx zip codes in under 30 minutes. From Broad Ripple walk-ups to 5,000 square foot Geist houses, we've seen the full range of Indy housing stock and the parking challenges that come with each.",
    highlights: [
      "Flat-rate pricing for moves between Columbus and Indianapolis",
      "Licensed for apartment-building loading docks and elevator reservations",
      "Dedicated downtown parking permit acquisition for restricted streets",
      "Most popular for: apartment moves in Broad Ripple, Fountain Square, Irvington",
    ],
    localNote:
      "Indianapolis apartment buildings often require certificates of insurance, elevator reservations, and 9am–5pm loading windows. We handle all of that paperwork for you and coordinate directly with your building manager — bring a key and point where the boxes go.",
    testimonial: {
      name: "David T.",
      neighborhood: "Meridian-Kessler, Indianapolis",
      text:
        "Moved out of a 1920s bungalow with a narrow side stair. The Swift crew brought extra floor runners and wrapped every banister. Zero damage, and they handled the COI for my new apartment building without me lifting a finger.",
    },
    faq: [
      {
        q: "Is there a flat rate for Columbus-to-Indianapolis moves?",
        a: "Yes. We publish fixed-price tiers based on home size (studio, 1 BR, 2 BR, 3+ BR) for moves between Columbus and the Indianapolis metro. Call for a quote.",
      },
      {
        q: "Do you provide a Certificate of Insurance (COI)?",
        a: "Yes, at no charge. Give us your building manager's email at booking and we'll send the COI directly to them before move day.",
      },
      {
        q: "Can you handle apartment moves with parking restrictions?",
        a: "Absolutely. For downtown and Broad Ripple buildings without loading docks, we work with Indy's parking permit office to reserve street space ahead of time.",
      },
    ],
  },
  {
    slug: "carmel",
    name: "Carmel",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 103,000",
    distanceFromHQ: "70 miles north — dedicated route with flat-rate pricing",
    zipCodes: ["46032", "46033", "46074", "46280", "46290"],
    neighborhoods: [
      "Carmel Arts & Design District",
      "Old Town Carmel",
      "West Clay",
      "Village of WestClay",
      "Brookshire",
      "Smokey Ridge",
      "The Hamlet",
    ],
    landmarks: [
      "The Palladium",
      "Clay Terrace",
      "Carmel Arts & Design District",
      "Monon Trail",
      "Carmel High School",
    ],
    intro:
      "Carmel consistently ranks among America's best places to live, and the housing stock reflects it: larger homes, finished basements stacked with gym equipment, and plenty of upscale furniture that needs to be moved carefully. Our Carmel crews specialize in high-value relocations — we disassemble Peloton bikes, crate large wall mirrors, and blanket-wrap hardwood furniture with the expectation that nothing leaves a mark on newly refinished floors.",
    highlights: [
      "Trained crews for high-value and luxury-furniture moves",
      "Specialty services: Peloton disassembly, art crating, piano moves",
      "Roundabout-route planning (Carmel has 140+ roundabouts)",
      "Most popular for: 4+ bedroom homes, West Clay and Brookshire relocations",
    ],
    localNote:
      "Carmel's roundabouts and private-drive HOAs can slow down a truck move if the driver isn't familiar with the layout. Our Carmel dispatch team maps out the route the day before and coordinates HOA notifications when required — no surprises on move morning.",
    testimonial: {
      name: "Priya K.",
      neighborhood: "Village of WestClay, Carmel",
      text:
        "Moved from a 3-story WestClay home to a new build in Zionsville. Swift handled the piano, two Pelotons, and a wine fridge — all with white-glove care. The move-out deep clean got our deposit back within a week.",
    },
    faq: [
      {
        q: "Do you handle high-value furniture and fragile items?",
        a: "Yes. Large mirrors, art, pianos, and specialty equipment like Peloton bikes are wrapped, crated, and handled by senior crew members trained for high-value moves.",
      },
      {
        q: "Can you work with HOAs and building regulations?",
        a: "Yes. We routinely coordinate with West Clay, Brookshire, and other Carmel HOAs, including truck parking permissions and timing windows.",
      },
      {
        q: "Do you offer storage between moves?",
        a: "Short-term storage (1–30 days) is available through our partner facilities. Ask your project manager when booking.",
      },
    ],
  },
  {
    slug: "fishers",
    name: "Fishers",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 99,000",
    distanceFromHQ: "75 miles north",
    zipCodes: ["46037", "46038", "46040", "46055"],
    neighborhoods: [
      "Nickel Plate District",
      "Geist Reservoir",
      "Brooks Chase",
      "Delaware Township",
      "Saxony",
      "The Maples",
    ],
    landmarks: [
      "Geist Reservoir",
      "Conner Prairie",
      "The Yard at Fishers District",
      "IKEA",
      "Fishers Nickel Plate District",
    ],
    intro:
      "Fishers has exploded from a small farming community into one of the fastest-growing suburbs in the Midwest. That growth means a lot of new construction moves — families relocating from Carmel, Indianapolis, or out-of-state into the 46037 and 46038 zips. Our Fishers crews know the Saxony, Brooks Chase, and Geist lake-adjacent communities well, including the HOA sign-in processes and the tight driveway configurations on waterfront lots.",
    highlights: [
      "New-construction move-in specialists (no scuffs on fresh drywall)",
      "Waterfront and lake-access move logistics for Geist properties",
      "Coordinated unload with interior-design delivery windows",
      "Most popular for: new-build move-ins, out-of-state relocations",
    ],
    localNote:
      "Fishers new-construction neighborhoods often have narrow drive approaches and fresh sod that trucks can ruin. We send a smaller box truck for final-mile delivery into tight lots, and we put plywood sheets over sod when we need to stage a heavier vehicle.",
    testimonial: {
      name: "Tom and Jenna R.",
      neighborhood: "Saxony, Fishers",
      text:
        "Moved from Chicago to Fishers and used Swift for the final-mile delivery out of storage. They coordinated with the builder's walk-through schedule and had everything placed by dinnertime. The team cared about our stuff like it was their own.",
    },
    faq: [
      {
        q: "Can you coordinate with a new-construction builder's schedule?",
        a: "Yes. We frequently schedule move-ins around final walk-throughs and utility activations. Give us the builder's contact at booking and we'll align timing.",
      },
      {
        q: "Do you have experience with lake-access or waterfront lots?",
        a: "We regularly move clients in the Geist and Morse areas. We use smaller trucks for tight waterfront drives and bring extra protection for lakefront landscaping.",
      },
      {
        q: "Do you offer move-in deep cleaning for new builds?",
        a: "Yes. Post-construction cleaning removes drywall dust, label residue, and protective film from appliances — book as a bundle for a discounted rate.",
      },
    ],
  },
  {
    slug: "greenwood",
    name: "Greenwood",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 65,000",
    distanceFromHQ: "35 miles north",
    zipCodes: ["46142", "46143"],
    neighborhoods: [
      "Downtown Greenwood",
      "Greenwood Park Mall area",
      "Old Town",
      "Suncrest",
      "Woodmen Estates",
      "Stones Crossing corridor",
    ],
    landmarks: [
      "Greenwood Park Mall",
      "Craig Park",
      "Main Street historic district",
      "Greenwood Airport",
      "Community Hospital South",
    ],
    intro:
      "Greenwood sits at the sweet spot between Columbus and Indianapolis — close enough to downtown Indy for commuters, far enough out to still feel suburban. Our Greenwood crews handle a steady mix: families moving up from Columbus, downsizers heading from Indy's south side, and plenty of rental turnovers near the Stones Crossing and Main Street corridors.",
    highlights: [
      "Flat-rate pricing for most Johnson County moves",
      "Rental-turnover specialists (move-out clean + property prep)",
      "Weekend scheduling availability",
      "Most popular for: 2-3 BR homes and apartment move-outs",
    ],
    localNote:
      "A lot of Greenwood housing is newer than Columbus stock, which means fewer plaster-wall and narrow-staircase issues — but HOA coverage is extensive. We pre-file any required HOA notifications for your neighborhood at no extra charge.",
    testimonial: {
      name: "Michelle B.",
      neighborhood: "Suncrest, Greenwood",
      text:
        "Swift moved us from a rental in Suncrest to a new home on the west side of town. They bundled the move-out clean with the move and we got our full deposit back. Easy, fair pricing, and zero drama.",
    },
    faq: [
      {
        q: "Do you offer move-out cleaning in Greenwood rentals?",
        a: "Yes. Many of our Greenwood clients bundle the move with move-out cleaning — it's the fastest way to recover a security deposit.",
      },
      {
        q: "Are you available on Sundays?",
        a: "Yes, for moves within 50 miles of Columbus. Sunday rates carry a small premium but availability is good for non-peak weekends.",
      },
    ],
  },
  {
    slug: "avon",
    name: "Avon",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 25,000",
    distanceFromHQ: "80 miles northwest",
    zipCodes: ["46123"],
    neighborhoods: [
      "Avon South",
      "Westwood",
      "Beechwood",
      "Landings at Prestwick",
    ],
    landmarks: [
      "Hendricks Regional Health",
      "Avon Town Hall Park",
      "Washington Township Park",
    ],
    intro:
      "Avon has seen fast-paced growth as Indianapolis commuters push west. We service Avon regularly, especially the newer subdivisions near Prestwick and the Beechwood/Hendricks Regional corridor. Most Avon moves we see are families stepping up from 2-bedroom to 4-bedroom homes — so packing volumes are usually larger than average.",
    highlights: [
      "Larger crews available for 4-5 bedroom family relocations",
      "Coordinated with Hendricks County utility providers for transfer timing",
      "Avon-specific local knowledge (back roads during US-36 rush hour)",
      "Most popular for: family-home upsizes, in-neighborhood moves",
    ],
    localNote:
      "US-36 through Avon is a well-known bottleneck during commute hours. We schedule Avon moves to start and end outside the 7–9am and 4–6pm windows whenever possible to keep your billable hours down.",
    testimonial: {
      name: "Greg and Sarah P.",
      neighborhood: "Prestwick, Avon",
      text:
        "Upgrading from a 3-BR to a 5-BR in Avon with three kids meant a lot of boxes. Swift brought a 4-person crew and got us moved and set up in one day. Worth every penny.",
    },
    faq: [
      {
        q: "Can you handle a same-neighborhood move in Avon?",
        a: "Absolutely. Short in-town moves are typically billed hourly with a 2-hour minimum, and they're often done in half a day.",
      },
      {
        q: "Is packing service available for family moves?",
        a: "Yes, including a 'kids' rooms only' option if parents want to pack the main living areas themselves.",
      },
    ],
  },
  {
    slug: "plainfield",
    name: "Plainfield",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 35,000",
    distanceFromHQ: "80 miles northwest",
    zipCodes: ["46168"],
    neighborhoods: [
      "Downtown Plainfield",
      "Andover",
      "Saratoga",
      "Sycamore Springs",
      "Vandalia Trail corridor",
    ],
    landmarks: [
      "Metropolis Mall",
      "Hummel Park",
      "Splash Island",
      "Plainfield Indianapolis International Airport corridor",
    ],
    intro:
      "Plainfield is a logistics and commercial hub — it's home to some of the largest distribution centers in the Midwest. That means we see a healthy mix of both residential family moves and commercial/warehouse-adjacent office relocations here. Whether you're moving a 3-bedroom home near Hummel Park or helping a growing logistics team relocate to a new Plainfield office, our crews know the territory.",
    highlights: [
      "Commercial move expertise for Plainfield logistics employers",
      "Weekend availability",
      "Flat-rate pricing for Plainfield-to-Indianapolis moves",
      "Most popular for: family moves + commercial office relocations",
    ],
    localNote:
      "If your move involves the airport-adjacent commercial corridor, we're experienced with the access rules and security check-ins that some warehouse complexes require. Give us the property details at booking and we'll handle the coordination.",
    testimonial: {
      name: "Angela L.",
      neighborhood: "Saratoga, Plainfield",
      text:
        "Moved from an apartment in Indianapolis to a home in Plainfield. The Swift team was professional, patient with my cat, and finished faster than quoted. Would use again in a heartbeat.",
    },
    faq: [
      {
        q: "Do you do commercial/office moves in Plainfield?",
        a: "Yes. We handle office relocations from small 10-person startups to mid-size teams, including IT equipment and workstation furniture.",
      },
      {
        q: "Can you accommodate security check-ins at warehouse complexes?",
        a: "Yes. Give us the facility contact ahead of time and we'll complete any required pre-clearance.",
      },
    ],
  },
  {
    slug: "zionsville",
    name: "Zionsville",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 30,000",
    distanceFromHQ: "85 miles north",
    zipCodes: ["46077"],
    neighborhoods: [
      "Historic Village of Zionsville",
      "Austin Oaks",
      "Brick Street area",
      "Cobblestone Lakes",
      "Eagle's Nest",
    ],
    landmarks: [
      "Zionsville Brick Street",
      "Lions Park",
      "Main Street shops",
      "Zionsville Golf Club",
      "SullivanMunce Cultural Center",
    ],
    intro:
      "Zionsville is a distinctive mix of historic Brick Street charm and large modern family homes. Our Zionsville moves skew larger — 4-5 bedroom homes with finished basements, extensive landscaping, and occasional historic-district protections on older Main Street properties. We assign senior crews to Zionsville jobs with extra attention to hardwood protection and landscaped approach paths.",
    highlights: [
      "Historic-home move expertise (original hardwoods, plaster walls)",
      "4+ bedroom family relocation specialists",
      "Coordinated white-glove unloading with interior design deliveries",
      "Most popular for: 4+ bedroom family moves",
    ],
    localNote:
      "The historic Brick Street section of downtown Zionsville has unique regulations about truck hours and delivery routes. If you're moving in or out of that area, we coordinate with the village office ahead of time to avoid any penalties.",
    testimonial: {
      name: "Christopher H.",
      neighborhood: "Austin Oaks, Zionsville",
      text:
        "Swift moved us from Chicago into a home in Zionsville. They worked around our furniture delivery schedule perfectly and treated every piece like it was irreplaceable. The crew lead personally walked the house with us before they left.",
    },
    faq: [
      {
        q: "Do you handle historic-home moves?",
        a: "Yes — older homes with original hardwood and plaster walls get specialized protection, and we use furniture pads rated for fragile interior finishes.",
      },
      {
        q: "Can you move large finished-basement setups?",
        a: "Absolutely. Home gyms, basement bars, and media rooms are among our most common Zionsville jobs.",
      },
    ],
  },
  {
    slug: "franklin",
    name: "Franklin",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 25,000",
    distanceFromHQ: "30 miles north",
    zipCodes: ["46131"],
    neighborhoods: [
      "Downtown Franklin",
      "Franklin College area",
      "Province Park neighborhood",
      "Hurricane Hills",
    ],
    landmarks: [
      "Franklin College",
      "Province Park",
      "Artcraft Theatre",
      "Johnson County Fairgrounds",
      "Johnson Memorial Hospital",
    ],
    intro:
      "Franklin keeps a true small-town feel despite its proximity to both Indianapolis and Columbus. We see a lot of older Franklin homes in and near the downtown historic district, along with newer subdivisions pushing out toward the fairgrounds. Our Franklin crews are comfortable with both — modern closets and narrow 1920s hall stairs alike.",
    highlights: [
      "Comfortable handling older-home stairways and tight hallways",
      "Partnership discounts for Franklin College student/faculty moves",
      "Most popular for: 2-3 BR family moves, downsizes near downtown",
    ],
    localNote:
      "Franklin's downtown historic district has some homes with original 100+ year old staircases. We allow extra time for these moves and bring tighter-profile furniture dollies so we can navigate without damaging original wood.",
    testimonial: {
      name: "Margaret S.",
      neighborhood: "Downtown Franklin",
      text:
        "Downsized from a 4-BR to a 2-BR downtown, and Swift handled the donation pickups as part of the move. They made one of the most stressful months of my life genuinely easy.",
    },
    faq: [
      {
        q: "Do you coordinate donation pickups during a downsize?",
        a: "Yes. We can haul unwanted items to Goodwill or Love Chapel on move day as part of the service — just tell us what goes where.",
      },
      {
        q: "Is there a discount for Franklin College affiliates?",
        a: "Yes, we offer a 10% faculty/student discount with a valid ID.",
      },
    ],
  },
  {
    slug: "bloomington",
    name: "Bloomington",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 80,000",
    distanceFromHQ: "60 miles southwest",
    zipCodes: ["47401", "47403", "47404", "47405", "47406", "47408"],
    neighborhoods: [
      "Downtown Bloomington",
      "Indiana University campus area",
      "Elm Heights",
      "Sherwood Oaks",
      "Prospect Hill",
      "Near West Side",
    ],
    landmarks: [
      "Indiana University",
      "Assembly Hall",
      "Kirkwood Avenue",
      "Monroe Lake",
      "IU Health Bloomington Hospital",
    ],
    intro:
      "Bloomington is one of our highest-volume markets outside Indianapolis, driven almost entirely by Indiana University's academic calendar. We run dedicated crews for the IU move-out wave in May and the move-in wave in August. If you're a student, a faculty member, or just someone moving in or out of the 47xxx zips, we've been doing this route for years and we know the campus-area logistics cold.",
    highlights: [
      "IU student housing move specialists (dorms and Greek houses)",
      "Flat-rate student moves for in-town Bloomington jobs",
      "Extended hours during May move-out and August move-in weeks",
      "Most popular for: IU student moves, faculty relocations",
    ],
    localNote:
      "IU move-out weekend in May is the single most chaotic weekend of our year. Book 4+ weeks ahead if you need service between May 5 and May 15, especially for dorms and Greek houses. We schedule tight back-to-back appointments to fit everyone in.",
    testimonial: {
      name: "Alex W.",
      neighborhood: "IU campus area, Bloomington",
      text:
        "Used Swift to move out of a 6-person Greek house during finals week. They were on time, respectful of our mess, and fit everything in one truck. The Venmo splitting made the group payment painless.",
    },
    faq: [
      {
        q: "Can you handle dorm and Greek-house moves?",
        a: "Yes. We do dozens of these per year and can issue per-student itemized receipts for easy group splitting.",
      },
      {
        q: "How far in advance do I need to book for IU move-out weekend?",
        a: "At least 4 weeks — ideally 6. May 5–15 fills up fast every year.",
      },
      {
        q: "Do you offer summer storage for IU students?",
        a: "Yes, through partner storage facilities. Book by early April for best rates.",
      },
    ],
  },
  {
    slug: "seymour",
    name: "Seymour",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 20,000",
    distanceFromHQ: "25 miles south",
    zipCodes: ["47274"],
    neighborhoods: [
      "Downtown Seymour",
      "Shields area",
      "West Seymour",
      "Hidden Creek",
    ],
    landmarks: [
      "Freeman Field",
      "Crossroads Community Park",
      "John Mellencamp's hometown",
      "Schneck Medical Center",
    ],
    intro:
      "Seymour is one of our closest service areas to Columbus — we can have a crew on the ground in about 30 minutes. We handle a mix of residential moves, farm-adjacent estate moves, and an increasing number of commercial moves as Seymour's downtown revitalization continues. Our Seymour clients appreciate straightforward pricing and crews that show up on time without excuses.",
    highlights: [
      "Sub-30-minute dispatch from Columbus",
      "Farm and estate move experience",
      "Most popular for: in-town moves, estate cleanouts",
    ],
    localNote:
      "Seymour has a fair number of older farmhouses on rural properties. We carry longer ramps and extra dollies for these jobs and can access most rural driveways with our standard box trucks.",
    testimonial: {
      name: "Bill and Doris T.",
      neighborhood: "West Seymour",
      text:
        "Cleaned out my late father's farmhouse and moved the family heirlooms to our place. The Swift team was respectful during a hard week and priced it fairly. Highly recommended.",
    },
    faq: [
      {
        q: "Do you handle estate moves and cleanouts?",
        a: "Yes. We move what you want to keep, donate what you flag for donation, and haul away the rest. All in one trip when possible.",
      },
      {
        q: "Can you access rural properties with long driveways?",
        a: "Yes. Our standard box trucks handle most rural drives. For very long or rough driveways we may use a smaller shuttle vehicle.",
      },
    ],
  },
  {
    slug: "shelbyville",
    name: "Shelbyville",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 20,000",
    distanceFromHQ: "40 miles northeast",
    zipCodes: ["46176"],
    neighborhoods: [
      "Downtown Shelbyville",
      "Blue River area",
      "Shelby County fairgrounds area",
    ],
    landmarks: [
      "Horseshoe Indianapolis (Indiana Grand)",
      "Blue River",
      "Major Health Partners hospital",
      "Shelby County Fairgrounds",
    ],
    intro:
      "Shelbyville mixes small-town living with easy access to both Indianapolis and Columbus. We handle a steady stream of Shelbyville moves — often families who work in Indy but choose Shelby County for the space and schools. Our crews know the back-road shortcuts between Shelbyville and the I-74 corridor that save real money on billable hours.",
    highlights: [
      "I-74 corridor route familiarity",
      "Flat-rate pricing for Shelbyville-to-Indianapolis moves",
      "Most popular for: family moves, in-town downsizes",
    ],
    localNote:
      "We time Shelbyville moves around the I-74 commute pattern — leaving before 7am or after 9am keeps you out of the Indianapolis rush hour bottleneck and shaves hours off the total time.",
    testimonial: {
      name: "Natalie F.",
      neighborhood: "Downtown Shelbyville",
      text:
        "The crew was early, friendly, and careful. Nothing broken, no surprises on the bill, and the move-out cleaning passed the landlord inspection the first time. Swift is a keeper.",
    },
    faq: [
      {
        q: "Can you bundle a Shelbyville move with a move-out clean?",
        a: "Yes. Most of our Shelbyville clients book both as a single service, which is cheaper than hiring them separately.",
      },
    ],
  },
  {
    slug: "greensburg",
    name: "Greensburg",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 12,000",
    distanceFromHQ: "40 miles east",
    zipCodes: ["47240"],
    neighborhoods: [
      "Downtown Greensburg (Tree City)",
      "North Greensburg",
      "Honda employee corridor",
    ],
    landmarks: [
      "Decatur County Courthouse (with the famous tree on top)",
      "Honda Manufacturing of Indiana",
      "Greensburg Public Library",
      "Decatur County Memorial Hospital",
    ],
    intro:
      "Greensburg is a compact, industrial-adjacent community anchored by Honda's manufacturing presence. We see a mix of Honda relocation moves (employees transferring into the Greensburg plant) and local residential moves. Our crews run a regular weekly route through Greensburg and can typically schedule a move with less than a week's notice.",
    highlights: [
      "Honda employee relocation experience",
      "Weekly dispatch route through Decatur County",
      "Most popular for: Honda relocations, in-town family moves",
    ],
    localNote:
      "Honda's Greensburg plant has specific relocation benefits and reimbursement structures. We provide Honda-friendly itemized invoices that match what their HR relocation team needs for approval.",
    testimonial: {
      name: "Kevin M.",
      neighborhood: "North Greensburg",
      text:
        "Relocated from Ohio for a Honda job. Swift had the right paperwork for my relocation reimbursement and delivered on schedule. Great experience.",
    },
    faq: [
      {
        q: "Do you handle Honda relocation moves?",
        a: "Yes. We're familiar with Honda's relocation reimbursement paperwork and provide invoices in the format their HR team needs.",
      },
    ],
  },
  {
    slug: "north-vernon",
    name: "North Vernon",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 6,500",
    distanceFromHQ: "25 miles southeast",
    zipCodes: ["47265"],
    neighborhoods: [
      "Downtown North Vernon",
      "Park Hills area",
      "Greenwood Terrace",
    ],
    landmarks: [
      "Muscatatuck Park",
      "North Vernon Airport",
      "Jennings County Courthouse",
    ],
    intro:
      "North Vernon is a tight-knit community close enough to Columbus that our crews can arrive within 30–40 minutes. We handle both in-town moves and cross-county relocations to and from Indianapolis and Louisville regularly. Most North Vernon moves we see are family transitions — adding kids, downsizing after retirement, or relocating for work.",
    highlights: [
      "30-minute dispatch from Columbus",
      "Cross-county route experience (Louisville, Cincinnati, Indianapolis)",
      "Most popular for: family moves, estate transitions",
    ],
    localNote:
      "Several North Vernon subdivisions are built on rolling hills with steep driveways. We send crews with experience navigating steep approaches and carry wheel chocks standard.",
    testimonial: {
      name: "Jessica O.",
      neighborhood: "Park Hills, North Vernon",
      text:
        "Moved from a hilly property in North Vernon to a flat lot in Columbus. Swift made the loading look easy even on our steep driveway. No complaints — just a job well done.",
    },
    faq: [
      {
        q: "Can you handle steep-driveway homes?",
        a: "Yes. Our standard crews are trained for hill-start loading and we bring wheel chocks on every rural move.",
      },
    ],
  },
  {
    slug: "sellersburg",
    name: "Sellersburg",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 9,500",
    distanceFromHQ: "80 miles south",
    zipCodes: ["47172"],
    neighborhoods: [
      "Downtown Sellersburg",
      "Ivy Hills",
      "Charlestown corridor",
    ],
    landmarks: [
      "Ivy Tech Sellersburg campus",
      "Silver Creek High School",
      "Louisville-area commuter corridor",
    ],
    intro:
      "Sellersburg is part of the Louisville metro's Indiana side, and most of our clients here are commuters working across the Ohio River. We run regular routes between Columbus, Indianapolis, and the Louisville-area towns, and we can handle cross-state moves between Indiana and Kentucky with zero paperwork headaches for you.",
    highlights: [
      "Indiana-to-Kentucky cross-state moves (licensed interstate)",
      "Louisville-area corridor familiarity",
      "Most popular for: cross-river moves, commuter relocations",
    ],
    localNote:
      "Cross-state moves between Indiana and Kentucky require interstate registration — we hold the proper USDOT authority so there's no paperwork delay for you. Quote covers all crossing fees.",
    testimonial: {
      name: "Randall P.",
      neighborhood: "Ivy Hills, Sellersburg",
      text:
        "Relocated from Louisville to Sellersburg. Swift handled the Kentucky-to-Indiana paperwork and the drive was smooth. My wife said she'd never use anyone else.",
    },
    faq: [
      {
        q: "Can you move my stuff across the Ohio River to Kentucky?",
        a: "Yes — we hold federal interstate authority and handle cross-state moves between IN and KY regularly.",
      },
    ],
  },
  {
    slug: "nashville",
    name: "Nashville",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 1,200",
    distanceFromHQ: "30 miles west",
    zipCodes: ["47448"],
    neighborhoods: [
      "Downtown Nashville (Brown County)",
      "Brown County State Park area",
    ],
    landmarks: [
      "Brown County State Park",
      "Brown County Playhouse",
      "Nashville artists' colony",
      "T.C. Steele State Historic Site",
    ],
    intro:
      "Nashville, Indiana — the arts colony at the heart of Brown County — is small but unique. Most of our Nashville moves involve art, antiques, craft inventory for shop owners, or cabin-to-full-time-home transitions. We treat every Nashville move with the extra care these valuable collections deserve, and we know the winding SR-46 route in and out of the Brown County hills.",
    highlights: [
      "Art, antique, and craft collection specialists",
      "Brown County hills route expertise",
      "Most popular for: artists, shop owners, seasonal-to-permanent residents",
    ],
    localNote:
      "The drive to Nashville is beautiful but winding, and Brown County's seasonal tourism traffic can slow move days. We schedule Nashville moves mid-week when possible and carry extra art-wrap on every truck.",
    testimonial: {
      name: "Helen R.",
      neighborhood: "Downtown Nashville, Brown County",
      text:
        "I run a gallery in Nashville and needed to relocate inventory to a new studio space. The Swift team wrapped every piece individually and asked questions before they touched anything fragile. Pros.",
    },
    faq: [
      {
        q: "Do you handle art and antique moves?",
        a: "Yes. Art and antiques are wrapped individually, crated when needed, and moved by senior crew members trained in high-value transport.",
      },
    ],
  },
  {
    slug: "danville",
    name: "Danville",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 10,500",
    distanceFromHQ: "90 miles northwest",
    zipCodes: ["46122"],
    neighborhoods: [
      "Downtown Danville",
      "Danville Community School District area",
    ],
    landmarks: [
      "Hendricks County Courthouse",
      "Ellis Park",
      "Historic downtown square",
    ],
    intro:
      "Danville is a quiet, historic town just west of Indianapolis — the kind of place where the courthouse square still anchors community life. We see mostly family moves here, including a lot of in-town relocations as families step up from starter homes to larger properties near the school district. Our Danville crews are respectful, quiet, and used to the slower pace of a small historic downtown.",
    highlights: [
      "Respectful small-town move etiquette",
      "Flat-rate pricing for most Hendricks County moves",
      "Most popular for: family moves, in-district upgrades",
    ],
    localNote:
      "Danville's historic downtown has limited truck parking — we unload at the nearest legal spot and cart items on dollies. No surprise fees for this; it's built into the flat-rate pricing.",
    testimonial: {
      name: "Laura J.",
      neighborhood: "Downtown Danville",
      text:
        "Swift moved us from a rental near the square to our first home in the school district. They took care on our new hardwood floors and didn't leave a scratch. We'll call them next time too.",
    },
    faq: [
      {
        q: "Do you charge extra for historic downtown parking challenges?",
        a: "No. If we need to park a block away and cart items in, that's built into your flat-rate quote — no surprise fees.",
      },
    ],
  },
  {
    slug: "martinsville",
    name: "Martinsville",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 12,000",
    distanceFromHQ: "60 miles northwest",
    zipCodes: ["46151"],
    neighborhoods: [
      "Downtown Martinsville",
      "Morgan Street corridor",
      "Foxcliff Estates",
      "Mahalasville Road area",
    ],
    landmarks: [
      "Morgan County Courthouse",
      "Morgan-Monroe State Forest (adjacent)",
      "Old City Park",
      "IU Health Morgan Hospital",
    ],
    intro:
      "Martinsville sits right on the I-69 corridor between Bloomington and Indianapolis, which makes it a magnet for commuters who want acreage without the metro price tag. We run into Martinsville every week — rural family moves, retirees downsizing out of the old artesian-well homes, and plenty of Foxcliff Estates relocations. Our crews know the back roads through Morgan County well enough to avoid the I-69 construction bottlenecks.",
    highlights: [
      "Rural property experience (long driveways, acreage lots)",
      "I-69 corridor route familiarity",
      "Most popular for: country-home moves, Foxcliff Estates, retiree downsizes",
    ],
    localNote:
      "Martinsville's mix of rural properties and older in-town homes means varying crew needs. For acreage properties we bring longer ramps and extra dollies; for the historic homes near downtown we use extra wall padding.",
    testimonial: {
      name: "Bruce K.",
      neighborhood: "Foxcliff Estates, Martinsville",
      text:
        "We moved from a condo in Carmel to a house on 4 acres in Foxcliff. Swift brought enough crew to handle a long carry from the driveway and still finished in one day. Professional all the way.",
    },
    faq: [
      {
        q: "Can you handle long rural driveways?",
        a: "Yes. We regularly service properties with 1/4-mile-plus driveways in Morgan County. Our standard trucks access most, and we bring a shuttle vehicle when needed.",
      },
      {
        q: "Do you service the Foxcliff Estates community?",
        a: "Yes — we know the gate procedures and roundabout layout and can coordinate directly with the HOA when required.",
      },
    ],
  },
  {
    slug: "ellettsville",
    name: "Ellettsville",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 6,700",
    distanceFromHQ: "65 miles southwest",
    zipCodes: ["47429"],
    neighborhoods: [
      "Downtown Ellettsville",
      "Richland Plaza area",
      "Heritage Woods",
      "Edgewood Hills",
    ],
    landmarks: [
      "Edgewood High School",
      "Lake Lemon",
      "Richland-Bean Blossom schools",
      "SR-46 corridor",
    ],
    intro:
      "Ellettsville is effectively Bloomington's quieter western neighbor — close enough to IU and the hospital system for commuters but with a distinctive small-town character. We handle a healthy mix of residential moves here, including a lot of families choosing Ellettsville for the Edgewood school district. Our Bloomington-area crews dispatch to Ellettsville the same day they cover IU moves.",
    highlights: [
      "Bloomington-metro same-day dispatch",
      "Edgewood school-district family-move expertise",
      "Most popular for: 3-4 BR family moves, IU-faculty relocations",
    ],
    localNote:
      "SR-46 through Ellettsville can bottleneck during IU events and Lake Lemon summer weekends. We schedule Ellettsville moves mid-morning or mid-afternoon to avoid peak congestion and keep your billable hours down.",
    testimonial: {
      name: "Dan H.",
      neighborhood: "Heritage Woods, Ellettsville",
      text:
        "We moved from a small place in Bloomington out to Heritage Woods with two kids under 4. The Swift crew was patient, careful with the crib and car seats, and finished by lunch. Couldn't have gone smoother.",
    },
    faq: [
      {
        q: "Do you service the Edgewood school district area?",
        a: "Yes, regularly. A large share of our Ellettsville moves are families relocating specifically for the school district.",
      },
      {
        q: "Can you bundle a move with a move-out clean?",
        a: "Yes. It's one of the most common combos we book in Monroe County — the move-out clean pays for itself in recovered deposits.",
      },
    ],
  },
  {
    slug: "whiteland",
    name: "Whiteland",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 4,500",
    distanceFromHQ: "30 miles north",
    zipCodes: ["46184"],
    neighborhoods: [
      "Downtown Whiteland",
      "Whiteland Heights",
      "Whispering Winds",
      "Tracy Road corridor",
    ],
    landmarks: [
      "Clark-Pleasant Middle School",
      "Whiteland Community High School",
      "Tracy Road / I-65 interchange",
    ],
    intro:
      "Whiteland is right on I-65 between Columbus and Indianapolis — one of our easiest routes. New subdivisions keep pushing out along Tracy Road and the Clark-Pleasant school district, which means we see a lot of first-time homebuyer moves here. Our Whiteland crews can usually schedule inside a week and roll in from Columbus in about 30 minutes.",
    highlights: [
      "Sub-30-minute dispatch from Columbus via I-65",
      "Clark-Pleasant school-district family-move specialists",
      "Most popular for: first-time homebuyer moves, apartment-to-home upgrades",
    ],
    localNote:
      "Whiteland subdivisions near Tracy Road are newer with tight cul-de-sac turnarounds. We send a smaller box truck when needed and pre-plan the turnaround so we don't hold up the neighbors.",
    testimonial: {
      name: "Katelyn R.",
      neighborhood: "Whispering Winds, Whiteland",
      text:
        "First home, first move. The Swift team walked us through the timing, gave us a realistic quote, and came in under budget. My husband and I both recommend them.",
    },
    faq: [
      {
        q: "How soon can you schedule a Whiteland move?",
        a: "Usually within a week for weekdays — sometimes 2-3 weeks for prime weekend slots during peak season.",
      },
    ],
  },
  {
    slug: "bargersville",
    name: "Bargersville",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 9,000",
    distanceFromHQ: "35 miles north",
    zipCodes: ["46106"],
    neighborhoods: [
      "Downtown Bargersville",
      "Cordell Glen",
      "Whiteland Road corridor",
      "Center Grove schools area",
    ],
    landmarks: [
      "Center Grove High School",
      "Bargersville Historic District",
      "Hoosier Heritage Port Authority rail corridor",
    ],
    intro:
      "Bargersville has grown fast thanks to the Center Grove school district — a rare combination of small-town feel with top-ranked schools. We see larger family moves here on average, often people relocating from Indianapolis specifically to land their kids in Center Grove. Swift's crews are familiar with the newer subdivisions along Whiteland Road and the tighter historic streets near the downtown core.",
    highlights: [
      "Center Grove school-district move specialists",
      "Larger-crew capacity for 4-5 BR family relocations",
      "Most popular for: Indy-to-Bargersville family relocations",
    ],
    localNote:
      "Center Grove-area moves tend to run larger than average (lots of kids' rooms, garage setups, and finished basements). We default to 3-4 person crews for Bargersville to avoid multi-trip overages.",
    testimonial: {
      name: "Rebecca N.",
      neighborhood: "Cordell Glen, Bargersville",
      text:
        "We moved from Indy to Center Grove for the schools. Swift handled our 4-BR move, a home gym, and a trampoline disassembly — all in one day. Well worth it.",
    },
    faq: [
      {
        q: "Do you disassemble trampolines, swing sets, and playground equipment?",
        a: "Yes. These are standard add-ons for family moves — let us know at booking so we bring the right tools.",
      },
    ],
  },
  {
    slug: "edinburgh",
    name: "Edinburgh",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 4,500",
    distanceFromHQ: "15 miles north",
    zipCodes: ["46124"],
    neighborhoods: [
      "Downtown Edinburgh",
      "Camp Atterbury area",
      "Edinburgh Premium Outlets area",
    ],
    landmarks: [
      "Edinburgh Premium Outlets",
      "Camp Atterbury Joint Maneuver Training Center",
      "Driftwood River",
      "Johnson Memorial Hospital network",
    ],
    intro:
      "Edinburgh sits just north of our Columbus home base — close enough that we can dispatch a crew in under 20 minutes. We handle a steady mix of Camp Atterbury relocations (military and civilian contractors move in and out regularly), residential moves near the Outlets corridor, and in-town downsizes. Military relocation paperwork is part of our weekly rhythm, and we know exactly what Camp Atterbury HR needs on an invoice.",
    highlights: [
      "Military and Camp Atterbury relocation specialists",
      "Sub-20-minute dispatch from Columbus",
      "DOD-compatible itemized invoicing available",
      "Most popular for: Camp Atterbury relocations, in-town moves",
    ],
    localNote:
      "Camp Atterbury has specific access and ID requirements. Give us the sponsor contact and visitor instructions at booking and our crew will be pre-cleared at the gate.",
    testimonial: {
      name: "Sgt. Marcus T.",
      neighborhood: "Edinburgh (Camp Atterbury area)",
      text:
        "Swift handled our PCS move to Edinburgh. The invoice paperwork matched exactly what my command needed for reimbursement — no corrections, no delays. Fast, careful, and reliable.",
    },
    faq: [
      {
        q: "Do you handle military (PCS) moves?",
        a: "Yes. We provide DOD-compatible itemized invoices and are familiar with Camp Atterbury's access and reimbursement paperwork.",
      },
      {
        q: "Can you dispatch same-day from Columbus?",
        a: "Often yes — Edinburgh is one of our closest service areas. Call by 10am and we can usually send a crew the same afternoon.",
      },
    ],
  },
  {
    slug: "fairland",
    name: "Fairland",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 1,500",
    distanceFromHQ: "45 miles northeast",
    zipCodes: ["46126"],
    neighborhoods: [
      "Downtown Fairland",
      "Triton Central schools area",
      "State Road 9 corridor",
    ],
    landmarks: [
      "Triton Central High School",
      "Shelby County rural corridor",
      "SR-9 / I-74 interchange",
    ],
    intro:
      "Fairland is a small community in northern Shelby County — tight-knit, rural, and anchored by the Triton Central school district. Most of our Fairland moves are families upgrading to larger acreage properties or in-town relocations within the school district. Our crews are comfortable with the rural-road logistics and the occasional farm estate move.",
    highlights: [
      "Rural property and farm-estate move experience",
      "Triton Central school-district family moves",
      "I-74 corridor route familiarity",
      "Most popular for: in-district upgrades, acreage relocations",
    ],
    localNote:
      "Fairland sits between two major commute routes (I-74 and SR-9). We schedule moves outside the 6:30-8:30am and 4:30-6:30pm commute windows to keep your billable hours down.",
    testimonial: {
      name: "Holly P.",
      neighborhood: "Fairland (Triton Central)",
      text:
        "Moved from a rental into our first home on 2 acres in Fairland. Swift was affordable, on time, and took great care of our daughter's nursery furniture. Strong recommend.",
    },
    faq: [
      {
        q: "Do you service small rural towns like Fairland?",
        a: "Yes. Our crews cover all of Shelby County regularly, including the rural areas around Triton Central.",
      },
    ],
  },
  {
    slug: "saint-paul",
    name: "Saint Paul",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 1,000",
    distanceFromHQ: "30 miles northeast",
    zipCodes: ["47272"],
    neighborhoods: [
      "Downtown Saint Paul",
      "Decatur County / Shelby County border area",
    ],
    landmarks: [
      "Flatrock River",
      "Decatur County farmland",
      "SR-244 corridor",
    ],
    intro:
      "Saint Paul is one of the smaller communities we serve — a true small town on the Decatur/Shelby county border. Our Saint Paul moves are mostly estate transitions, retiree downsizes, and rural family relocations. We treat small-town moves the same way we treat big-city ones: on time, careful with your belongings, and fair with the invoice.",
    highlights: [
      "Small-town respectful move etiquette",
      "Estate and retiree downsize experience",
      "Most popular for: in-town downsizes, estate moves",
    ],
    localNote:
      "Saint Paul's older homes often have original staircases and narrow doorways. We measure twice and allow extra time so nothing gets scratched on the way out.",
    testimonial: {
      name: "Don and Marlene H.",
      neighborhood: "Downtown Saint Paul",
      text:
        "Downsized from the home we raised our kids in for 40 years. Swift handled some very sentimental pieces with real care. They made a hard transition feel smooth.",
    },
    faq: [
      {
        q: "Do you service towns as small as Saint Paul?",
        a: "Absolutely. No minimum population — we serve all of Decatur and Shelby counties with the same care and pricing as our larger markets.",
      },
    ],
  },
  // ─── Close-in towns (≤30 min from Columbus HQ) — full moving + cleaning ───
  {
    slug: "hope",
    name: "Hope",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 2,100",
    distanceFromHQ: "10 miles east — same-day moving & cleaning",
    zipCodes: ["47246"],
    neighborhoods: [
      "Town Square",
      "Salt Creek corridor",
      "Flatrock Township",
    ],
    landmarks: [
      "Hope Town Square",
      "Yellow Trail Museum",
      "Hauser Jr-Sr High School",
    ],
    intro:
      "Hope sits just east of Columbus — close enough that a Swift crew can be at your door in under 15 minutes. The town's historic square and tight-knit neighborhoods mean we see a lot of first-home moves, downsizes, and in-town cleanings here. Because Hope is inside our 30-minute zone, every service we offer (moving, cleaning, packing, move-out deep cleans) runs here at our standard pricing with zero travel fee.",
    highlights: [
      "Sub-15-minute dispatch from Columbus HQ",
      "Full residential + commercial cleaning coverage",
      "No travel fee — same rates as Columbus",
      "Most popular for: in-town moves, recurring cleaning plans",
    ],
    localNote:
      "Hope's historic town square has limited loading access. Our crews know to stage the truck on the side streets and cart items in — no surprise fees, it's part of our flat rate.",
    testimonial: {
      name: "Angela R.",
      neighborhood: "Near Town Square, Hope",
      text:
        "We've used Swift twice — once for the move and now for monthly cleaning. Being 10 minutes from their shop means they show up fast and there's never a scheduling squeeze.",
    },
    faq: [
      {
        q: "Do you offer recurring cleaning in Hope?",
        a: "Yes — weekly, bi-weekly, and monthly plans are all available with no travel surcharge.",
      },
      {
        q: "How quickly can you dispatch to Hope?",
        a: "Often the same day for weekday moves or cleanings. Call by 10am for same-afternoon service when crew availability allows.",
      },
    ],
  },
  {
    slug: "taylorsville",
    name: "Taylorsville",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 1,000",
    distanceFromHQ: "10 miles north — same-day moving & cleaning",
    zipCodes: ["47280"],
    neighborhoods: [
      "US-31 corridor",
      "Haw Creek Township",
      "North Taylorsville",
    ],
    landmarks: [
      "Old US-31",
      "Haw Creek",
      "Bakalar Air Force Station (historic)",
    ],
    intro:
      "Taylorsville is effectively a northern neighborhood of Columbus — a quiet US-31 stop with easy access to both I-65 and downtown. Because it's inside our 30-minute zone, Taylorsville clients get the full Swift service menu: moving, cleaning, packing, and recurring cleaning plans at our standard Columbus pricing. Most of our Taylorsville jobs are family moves between subdivisions along US-31 and weekly cleaning routes for commuters.",
    highlights: [
      "Sub-15-minute dispatch from Columbus",
      "Full residential cleaning coverage (weekly, bi-weekly, monthly)",
      "No travel fee — same rates as Columbus",
      "Most popular for: US-31 subdivision moves, recurring house cleaning",
    ],
    localNote:
      "Taylorsville subdivisions along US-31 often have narrow cul-de-sac access. We send a smaller box truck for final unload when needed so we don't block neighbors.",
    testimonial: {
      name: "Mark and Jessica B.",
      neighborhood: "US-31 corridor, Taylorsville",
      text:
        "Swift handled our move from an apartment in Columbus to our first house in Taylorsville. Same team now cleans twice a month. Consistent, reliable, friendly.",
    },
    faq: [
      {
        q: "Is there a travel fee from Columbus to Taylorsville?",
        a: "No — Taylorsville is inside our zero-travel-fee zone. You pay the same hourly rate as a Columbus-to-Columbus job.",
      },
    ],
  },
  {
    slug: "elizabethtown",
    name: "Elizabethtown",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 500",
    distanceFromHQ: "8 miles south — same-day moving & cleaning",
    zipCodes: ["47232"],
    neighborhoods: [
      "Downtown Elizabethtown",
      "East Fork White River corridor",
      "Sand Creek Township",
    ],
    landmarks: [
      "East Fork White River",
      "Flat Rock Road farmland",
      "Sand Creek",
    ],
    intro:
      "Elizabethtown is a tight little community just south of Columbus — fewer than 10 miles from our HQ. Because it's well inside our 30-minute dispatch zone, every Swift service is available here at our standard Columbus rates: moves, deep cleans, recurring cleaning plans, and packing. Our Elizabethtown jobs are mostly rural family relocations, estate cleanouts, and farmhouse cleanings.",
    highlights: [
      "Sub-15-minute dispatch from Columbus HQ",
      "Full cleaning menu — weekly, deep, move-out",
      "Rural property & long-driveway experience",
      "Most popular for: rural family moves, estate cleanings",
    ],
    localNote:
      "Many Elizabethtown homes sit on acreage with long driveways. Our standard trucks access most; for rougher rural drives we bring a shuttle vehicle at no extra charge.",
    testimonial: {
      name: "Carol T.",
      neighborhood: "Sand Creek Township, Elizabethtown",
      text:
        "We moved out of my mother-in-law's farmhouse and Swift did the full estate clean after. Kind, thorough, and the price was exactly what they quoted.",
    },
    faq: [
      {
        q: "Do you handle rural properties in Elizabethtown?",
        a: "Yes, regularly. Acreage, long driveways, barns, and outbuildings are all within our standard service.",
      },
    ],
  },
  // ─── Mid-range (30-90 min) — moving services ───
  {
    slug: "madison",
    name: "Madison",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 12,000",
    distanceFromHQ: "55 miles southeast — about 70 minutes",
    zipCodes: ["47250"],
    neighborhoods: [
      "Downtown Madison (Historic District)",
      "North Madison",
      "Hanover area",
      "Clifty Falls area",
    ],
    landmarks: [
      "Madison Historic District (largest contiguous historic district in the US)",
      "Clifty Falls State Park",
      "Ohio River waterfront",
      "Hanover College",
    ],
    intro:
      "Madison is one of Indiana's most beautiful river towns — a National Historic Landmark district with streets full of 19th-century homes facing the Ohio River. We run moves to and from Madison regularly, especially for retirees relocating into the historic district and Hanover College faculty transitions. Historic-home moves here require extra care: original hardwood floors, plaster walls, and narrow period staircases are the norm.",
    highlights: [
      "Historic-home move specialists (plaster walls, original hardwood)",
      "Hanover College faculty & staff relocation experience",
      "Extra wall padding on every Madison job",
      "Most popular for: retiree relocations, historic-district moves",
    ],
    localNote:
      "Madison's National Historic Landmark district has strict preservation rules for contractors and movers. We coordinate with the Madison Historic District office when required and always use non-marking floor protection.",
    testimonial: {
      name: "Eleanor C.",
      neighborhood: "Downtown Historic District, Madison",
      text:
        "Swift moved us into an 1840s row house with original plaster walls and a tight spiral stair. Zero damage. They felt more like restoration specialists than movers.",
    },
    faq: [
      {
        q: "Do you have experience with Madison's historic district homes?",
        a: "Yes — we send senior crews to Madison historic-district jobs and carry plaster-rated padding standard.",
      },
      {
        q: "Can you do a Columbus-to-Madison relocation in one day?",
        a: "Yes, for most 2-3 bedroom homes. Larger homes may need a 2-truck deployment or a second day for setup.",
      },
    ],
  },
  {
    slug: "bedford",
    name: "Bedford",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 13,000",
    distanceFromHQ: "50 miles southwest — about 70 minutes",
    zipCodes: ["47421"],
    neighborhoods: [
      "Downtown Bedford",
      "Lincoln Avenue corridor",
      "Oolitic area (adjacent)",
      "Bedford North Lawrence school area",
    ],
    landmarks: [
      "Lawrence County Courthouse",
      "Indiana Limestone quarries",
      "Lawrence County Museum",
      "IU Health Bedford Hospital",
    ],
    intro:
      "Bedford — the 'Limestone Capital of the World' — is anchored by its quarry heritage and the Bedford North Lawrence school district. We handle a steady mix of residential relocations here, often commuters heading to Bloomington or Columbus for work, plus estate moves as generations transition in and out of limestone-built family homes.",
    highlights: [
      "Limestone-era home experience (solid stone walls, deep windowsills)",
      "Bedford-to-Bloomington commuter relocation specialists",
      "Standard pricing tier for Columbus-to-Bedford moves",
      "Most popular for: family relocations, commuter moves",
    ],
    localNote:
      "Bedford's older homes are built with solid limestone — gorgeous but occasionally tricky to move large furniture through (deep windowsills, thick door jambs). We measure ahead of time and disassemble when needed.",
    testimonial: {
      name: "Gary W.",
      neighborhood: "Lincoln Avenue, Bedford",
      text:
        "Moved from Bloomington into a limestone-built house in Bedford. Swift disassembled a king bed to get it through the narrow front door — other movers had told us it wouldn't fit. Very impressed.",
    },
    faq: [
      {
        q: "Is Bedford inside your standard service radius?",
        a: "Yes. We have a published flat-rate tier for Columbus-Bedford moves based on home size.",
      },
    ],
  },
  {
    slug: "scottsburg",
    name: "Scottsburg",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 7,000",
    distanceFromHQ: "50 miles south — about 65 minutes",
    zipCodes: ["47170"],
    neighborhoods: [
      "Downtown Scottsburg",
      "Scott Memorial Hospital area",
      "I-65 corridor",
    ],
    landmarks: [
      "Scott County Courthouse",
      "Scottsburg City Park",
      "I-65 / US-31 interchange",
    ],
    intro:
      "Scottsburg is a convenient I-65 stop between Columbus and Louisville — an easy 65-minute dispatch from our HQ. Most of our Scottsburg moves are commuter families heading to Louisville-area jobs or retirees relocating into quieter Scott County neighborhoods. We run the I-65 corridor weekly, so Scottsburg is on our regular route.",
    highlights: [
      "Weekly I-65 route through Scott County",
      "Flat-rate pricing for Scottsburg-to-Louisville moves",
      "Most popular for: commuter relocations, retiree downsizes",
    ],
    localNote:
      "We schedule Scottsburg moves outside of the I-65 rush windows (7-9am northbound, 4-6pm southbound) — it keeps billable hours down and avoids construction bottlenecks around Seymour.",
    testimonial: {
      name: "Jim M.",
      neighborhood: "Downtown Scottsburg",
      text:
        "Swift moved us from Scottsburg to a new job in Carmel — about 3 hours of driving total. The crew was cheerful start to finish and the quote held exactly.",
    },
    faq: [
      {
        q: "Do you handle Scottsburg-to-Louisville moves?",
        a: "Yes, regularly. We hold interstate authority so cross-state IN/KY moves have no extra paperwork for you.",
      },
    ],
  },
  {
    slug: "new-albany",
    name: "New Albany",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 38,000",
    distanceFromHQ: "80 miles south — about 105 minutes",
    zipCodes: ["47150"],
    neighborhoods: [
      "Downtown New Albany",
      "Mansion Row Historic District",
      "Silver Hills",
      "Grant Line Road corridor",
    ],
    landmarks: [
      "Culbertson Mansion State Historic Site",
      "Ohio River waterfront",
      "Indiana University Southeast",
      "Mansion Row Historic District",
    ],
    intro:
      "New Albany is one of the biggest Indiana cities we serve — the north anchor of the Louisville metro. We see heavy demand here: Mansion Row historic-district moves, IU Southeast faculty relocations, and plenty of cross-river moves between Indiana and Kentucky. Our New Albany jobs are usually larger than average and often involve cross-state logistics we handle end-to-end.",
    highlights: [
      "Indiana-to-Kentucky cross-river move specialists (licensed interstate)",
      "Mansion Row historic-district experience",
      "IU Southeast faculty/staff relocation route",
      "Most popular for: cross-metro moves, historic-home relocations",
    ],
    localNote:
      "Cross-river moves between New Albany and Louisville require USDOT interstate authority, which we hold. No extra paperwork or fees from you — it's built into the standard quote.",
    testimonial: {
      name: "Heather L.",
      neighborhood: "Mansion Row, New Albany",
      text:
        "Swift moved us from an 1870s row house in Mansion Row to a new home across the river in Louisville. Every piece of furniture arrived in the same condition it left. Professional from start to finish.",
    },
    faq: [
      {
        q: "Can you do cross-river moves to Kentucky?",
        a: "Yes. We hold federal interstate authority and handle IN-to-KY relocations every week.",
      },
      {
        q: "Do you service the Mansion Row historic district?",
        a: "Yes — senior crews only, plus extra wall and floor protection for these gorgeous but fragile homes.",
      },
    ],
  },
  {
    slug: "jeffersonville",
    name: "Jeffersonville",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 50,000",
    distanceFromHQ: "75 miles south — about 100 minutes",
    zipCodes: ["47129", "47130"],
    neighborhoods: [
      "Downtown Jeffersonville",
      "Old Jeffersonville Historic District",
      "Oak Park",
      "NoCo Arts District",
    ],
    landmarks: [
      "Big Four Bridge pedestrian span",
      "Howard Steamboat Museum",
      "Ohio River waterfront",
      "Clark Memorial Bridge",
    ],
    intro:
      "Jeffersonville — directly across the Ohio River from downtown Louisville — is one of the busier Louisville-metro markets we serve. We run regular Jeffersonville routes for Louisville-area commuters, Ohio River waterfront moves, and historic-district relocations near the NoCo Arts corridor. Cross-state IN/KY moves are our specialty and we handle every paperwork detail.",
    highlights: [
      "Cross-river Indiana-to-Kentucky move expertise",
      "Historic-district experience (Old Jeffersonville)",
      "NoCo Arts District & downtown loft moves",
      "Most popular for: cross-metro relocations, downtown Louisville commuters",
    ],
    localNote:
      "Downtown Jeffersonville lofts and NoCo Arts-area condos often lack loading docks. We coordinate with building managers for elevator reservations and bring extra floor protection for polished concrete and hardwood.",
    testimonial: {
      name: "Tyler and Rachel J.",
      neighborhood: "NoCo Arts District, Jeffersonville",
      text:
        "We moved from a loft in NoCo to a house in Prospect, KY. Swift handled the bridge crossing and building elevator coordination. Smoothest move we've ever done.",
    },
    faq: [
      {
        q: "Can you coordinate with downtown loft building managers?",
        a: "Yes. COI documents, elevator reservations, and loading-dock coordination are all part of the standard service.",
      },
    ],
  },
  {
    slug: "clarksville",
    name: "Clarksville",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 22,000",
    distanceFromHQ: "75 miles south — about 100 minutes",
    zipCodes: ["47129"],
    neighborhoods: [
      "Downtown Clarksville",
      "Greentree Mall area",
      "Veterans Parkway corridor",
      "Falls of the Ohio area",
    ],
    landmarks: [
      "Falls of the Ohio State Park",
      "Greentree Mall",
      "Lewis and Clark statue",
      "Veterans Parkway",
    ],
    intro:
      "Clarksville rounds out the Indiana side of the Louisville metro, sitting between Jeffersonville and New Albany along Veterans Parkway. Our Clarksville moves are a mix of family relocations into the Greentree Mall corridor and commercial moves for businesses along I-65 / Veterans Parkway. Being a Louisville-metro town means cross-state moves are common, and we have all the interstate credentials to handle them without paperwork headaches.",
    highlights: [
      "Licensed interstate authority for IN/KY cross-river moves",
      "Commercial & retail-corridor move expertise",
      "Weekly route through the Louisville metro",
      "Most popular for: family moves, commercial/retail relocations",
    ],
    localNote:
      "Veterans Parkway and the Greentree Mall corridor get congested midday. We schedule Clarksville loads for early morning starts to keep billable hours efficient.",
    testimonial: {
      name: "Marcus D.",
      neighborhood: "Veterans Parkway, Clarksville",
      text:
        "Swift moved our small business from Clarksville to a bigger space across the river in Louisville. They coordinated with our IT contractor for the server crates and finished in one day. Highly recommend.",
    },
    faq: [
      {
        q: "Do you do small-business / commercial moves in Clarksville?",
        a: "Yes. Office and retail relocations of all sizes, including IT equipment and specialty coordination.",
      },
    ],
  },
  {
    slug: "richmond",
    name: "Richmond",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 35,000",
    distanceFromHQ: "80 miles east — about 90 minutes",
    zipCodes: ["47374"],
    neighborhoods: [
      "Downtown Richmond (Depot District)",
      "Glen Miller Park area",
      "Earlham College area",
      "East Main Street corridor",
    ],
    landmarks: [
      "Earlham College",
      "Indiana University East",
      "Glen Miller Park",
      "Richmond Art Museum",
      "Wayne County Historical Museum",
    ],
    intro:
      "Richmond is an established east-Indiana city with two colleges (Earlham and IU East) anchoring a steady academic-relocation market. We run Richmond moves regularly, including faculty transitions, Earlham student moves, and Ohio-border residential relocations. The Interstate 70 corridor makes Richmond a straightforward 90-minute dispatch from our HQ.",
    highlights: [
      "Earlham College & IU East faculty move route",
      "Indiana-to-Ohio cross-state experience (Richmond is on the border)",
      "I-70 corridor familiarity",
      "Most popular for: academic relocations, cross-state moves to Ohio",
    ],
    localNote:
      "Richmond sits directly on the Ohio border. Moves continuing east into Ohio (Dayton, Cincinnati) are considered interstate — we hold the USDOT authority and handle all paperwork as part of the quote.",
    testimonial: {
      name: "Prof. Daniel H.",
      neighborhood: "Earlham College area, Richmond",
      text:
        "Swift moved me from a rental near Earlham to a new house in Centerville, OH. They handled the cross-state paperwork without me lifting a finger. Polite, on-time, and careful with my book collection.",
    },
    faq: [
      {
        q: "Can you do Indiana-to-Ohio moves from Richmond?",
        a: "Yes — we hold federal interstate authority and run the IN-to-OH route weekly.",
      },
      {
        q: "Do you work with Earlham College faculty and staff?",
        a: "Yes. We've moved faculty members during summer transitions for years and can coordinate with Earlham's HR relocation contact.",
      },
    ],
  },
  {
    slug: "batesville",
    name: "Batesville",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 6,500",
    distanceFromHQ: "50 miles east — about 60 minutes",
    zipCodes: ["47006"],
    neighborhoods: [
      "Downtown Batesville",
      "Margaret Mary Health area",
      "Hillenbrand corridor",
    ],
    landmarks: [
      "Margaret Mary Health hospital",
      "Hillenbrand Industries campus",
      "Batesville Memorial Park",
      "I-74 / SR-229 interchange",
    ],
    intro:
      "Batesville is a small manufacturing town anchored by Hillenbrand and Margaret Mary Health — both major employers driving steady relocation demand. We handle executive relocations into Hillenbrand, medical-staff transitions at Margaret Mary, and plenty of family moves along the I-74 corridor. Batesville is one hour east of our HQ on a clear drive.",
    highlights: [
      "Hillenbrand and Margaret Mary Health relocation experience",
      "I-74 corridor route familiarity",
      "Medical-staff / shift-work friendly scheduling",
      "Most popular for: executive relocations, medical-staff transitions",
    ],
    localNote:
      "Margaret Mary Health's nursing schedules mean we often do Batesville moves on odd timing — early morning, late evening, or weekends. We schedule around your shift pattern, not ours.",
    testimonial: {
      name: "Nurse Jamie L.",
      neighborhood: "Batesville (Margaret Mary area)",
      text:
        "Moved from Cincinnati to Batesville for a new role at Margaret Mary. Swift did an evening load-in after my shift so I didn't have to burn a PTO day. Little things like that matter.",
    },
    faq: [
      {
        q: "Can you work around nursing / medical shift schedules?",
        a: "Yes — we regularly schedule moves at 6am, 8pm, or on weekends to accommodate shift workers.",
      },
    ],
  },
  {
    slug: "versailles",
    name: "Versailles",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 2,000",
    distanceFromHQ: "35 miles east — about 45 minutes",
    zipCodes: ["47042"],
    neighborhoods: [
      "Downtown Versailles",
      "Versailles State Park area",
      "Ripley County corridor",
    ],
    landmarks: [
      "Versailles State Park",
      "Ripley County Courthouse",
      "Tyson Library",
      "SR-50 corridor",
    ],
    intro:
      "Versailles is the Ripley County seat — a small but historic town known for Versailles State Park and the lakeside communities around Versailles Lake. Our Versailles moves are a mix of rural family relocations, lake-cabin seasonal-to-permanent transitions, and estate moves for longtime county residents. We run SR-50 east from Columbus weekly.",
    highlights: [
      "Rural property & long-driveway experience",
      "Lake-cabin and seasonal-home move expertise",
      "SR-50 corridor route familiarity",
      "Most popular for: rural family moves, cabin-to-permanent transitions",
    ],
    localNote:
      "The Versailles Lake area has some gated subdivisions and narrow shoreline roads. We dispatch smaller trucks for lakeside loads and coordinate HOA access the day before.",
    testimonial: {
      name: "Greg T.",
      neighborhood: "Versailles Lake area",
      text:
        "Converted our lake cabin to a full-time home. Swift moved us from Indianapolis in one trip and navigated the tight lakeside driveway without issue. Great experience.",
    },
    faq: [
      {
        q: "Do you access the Versailles Lake subdivisions?",
        a: "Yes, regularly. We know the gate codes and narrow-road layouts and bring smaller trucks when required.",
      },
    ],
  },
  {
    slug: "rushville",
    name: "Rushville",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 6,000",
    distanceFromHQ: "40 miles northeast — about 50 minutes",
    zipCodes: ["46173"],
    neighborhoods: [
      "Downtown Rushville",
      "Rush County Schools area",
      "US-52 corridor",
    ],
    landmarks: [
      "Rush County Courthouse",
      "Riverside Park",
      "Rushville Consolidated High School",
    ],
    intro:
      "Rushville is a tight agricultural community northeast of Columbus — home to Rush County's farm families and anchored by a classic downtown courthouse square. Our Rushville moves are mostly farm-estate transitions, in-town family moves, and the occasional commercial relocation along US-52. We run the SR-3 / US-52 corridor regularly.",
    highlights: [
      "Farm-estate and rural-property move experience",
      "US-52 corridor familiarity",
      "Most popular for: farm estate moves, in-town family relocations",
    ],
    localNote:
      "Rushville farm estates often include outbuildings, workshops, and equipment — more than a standard residential load. We scope these moves in advance so the quote covers everything.",
    testimonial: {
      name: "The Meyer Family",
      neighborhood: "Rural Rushville",
      text:
        "When we sold the family farm, Swift did the full estate move in a single weekend — house contents, workshop tools, and sentimental items. They were patient through every decision.",
    },
    faq: [
      {
        q: "Can you handle farm-estate moves with outbuildings?",
        a: "Yes. We scope the full property before quoting so there are no day-of surprises.",
      },
    ],
  },
  {
    slug: "salem",
    name: "Salem",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 6,500",
    distanceFromHQ: "70 miles south — about 90 minutes",
    zipCodes: ["47167"],
    neighborhoods: [
      "Downtown Salem",
      "Washington County Courthouse area",
      "SR-56 / SR-135 corridor",
    ],
    landmarks: [
      "Washington County Courthouse",
      "John Hay Center",
      "Salem Speedway",
    ],
    intro:
      "Salem is the Washington County seat — a classic Indiana small town with a courthouse-square downtown and easy SR-135 access north to Columbus or south to Louisville. Our Salem moves are commuter relocations and retiree downsizes, usually into older homes around the courthouse square. We run the SR-135 / SR-56 route about once a week.",
    highlights: [
      "SR-135 / SR-56 corridor familiarity",
      "Older courthouse-square home experience",
      "Most popular for: commuter moves, retiree downsizes",
    ],
    localNote:
      "Salem's courthouse square has street-parking constraints during market days. We schedule moves outside of Saturday-morning market hours to avoid bottlenecks.",
    testimonial: {
      name: "Dianne K.",
      neighborhood: "Courthouse Square, Salem",
      text:
        "Downsized from a big house to a small place near the square. Swift handled donation pickups and the move in one day. Exactly what I needed.",
    },
    faq: [
      {
        q: "Do you do donation pickups during a downsize move?",
        a: "Yes — we haul items to Goodwill or a donation center of your choice as part of the move.",
      },
    ],
  },
  {
    slug: "lawrenceburg",
    name: "Lawrenceburg",
    state: "Indiana",
    stateAbbr: "IN",
    population: "About 5,000",
    distanceFromHQ: "80 miles east — about 100 minutes",
    zipCodes: ["47025"],
    neighborhoods: [
      "Downtown Lawrenceburg",
      "Newtown Historic District",
      "Ohio River waterfront",
    ],
    landmarks: [
      "Hollywood Casino Lawrenceburg",
      "Lawrenceburg Speedway",
      "Ohio River waterfront",
      "Dearborn County Courthouse",
    ],
    intro:
      "Lawrenceburg is an Ohio River town directly on the Indiana-Ohio border — about 100 minutes east of our HQ. We see steady demand here: Hollywood Casino staff relocations, Newtown historic-district moves, and cross-state IN-to-OH moves continuing into the Cincinnati metro. Cross-state logistics are our standard service here.",
    highlights: [
      "Indiana-to-Ohio cross-state moves (Cincinnati metro)",
      "Ohio River waterfront & historic-district experience",
      "Hollywood Casino staff relocation route",
      "Most popular for: cross-metro moves, historic-district relocations",
    ],
    localNote:
      "Lawrenceburg's Newtown Historic District has strict regulations on contractor access and truck hours. We coordinate with the city's preservation office the week before any Newtown job.",
    testimonial: {
      name: "Keisha B.",
      neighborhood: "Newtown Historic District, Lawrenceburg",
      text:
        "Moved from an 1880s home in Newtown to a new place across the river in West Chester, OH. Swift handled the historic-district permits and the interstate paperwork. Worth every penny.",
    },
    faq: [
      {
        q: "Do you handle Indiana-to-Ohio moves from Lawrenceburg?",
        a: "Yes — Cincinnati-metro cross-state moves are a regular route for us.",
      },
    ],
  },
  {
    slug: "louisville",
    name: "Louisville",
    state: "Kentucky",
    stateAbbr: "KY",
    population: "About 625,000",
    distanceFromHQ: "75 miles south — about 90 minutes from Columbus",
    zipCodes: ["40202", "40203", "40204", "40205", "40206", "40207", "40208", "40222", "40241", "40245"],
    neighborhoods: [
      "Downtown Louisville",
      "NuLu",
      "The Highlands",
      "St. Matthews",
      "Crescent Hill",
      "Middletown",
      "Jeffersontown",
    ],
    landmarks: [
      "Churchill Downs",
      "Louisville Slugger Museum",
      "Waterfront Park",
      "University of Louisville",
      "Muhammad Ali Center",
    ],
    intro:
      "Louisville is one of the biggest out-of-state markets we serve from Columbus. Because the drive is straightforward down I-65, we handle a steady flow of residential and commercial moves between Columbus, southern Indiana, and the Louisville metro. Our Louisville clients usually need reliable interstate coordination, careful high-rise or historic-home handling, and a crew that can manage cross-river logistics without making the move feel complicated.",
    highlights: [
      "Regular Columbus-to-Louisville route coverage",
      "Licensed interstate moving across Indiana and Kentucky",
      "Downtown apartment, condo, and elevator-move experience",
      "Most popular for: cross-state relocations, family moves, office transitions",
    ],
    localNote:
      "Louisville moves can vary a lot block to block — downtown towers, older Highlands homes, and suburban subdivisions all require different loading plans. We confirm truck access, elevator timing, and parking details before move day so the interstate part feels routine instead of stressful.",
    testimonial: {
      name: "Ashley and Marcus P.",
      neighborhood: "The Highlands, Louisville",
      text:
        "We moved from Columbus to Louisville for work and Swift handled the entire transition without a hitch. The crew was organized, fast, and knew exactly how to coordinate the cross-state details.",
    },
    faq: [
      {
        q: "Do you handle moves between Columbus and Louisville?",
        a: "Yes. Columbus-to-Louisville and Louisville-to-Columbus moves are one of our regular cross-state routes.",
      },
      {
        q: "Can you move us across the Indiana-Kentucky state line?",
        a: "Yes — we are licensed for interstate moving and handle Indiana-to-Kentucky paperwork and logistics as part of the quote.",
      },
      {
        q: "Do you serve the surrounding Louisville metro too?",
        a: "Yes. We can usually help throughout the nearby Louisville metro on a case-by-case basis, especially communities close to our regular southern Indiana route.",
      },
    ],
  },
  {
    slug: "cincinnati",
    name: "Cincinnati",
    state: "Ohio",
    stateAbbr: "OH",
    population: "About 310,000",
    distanceFromHQ: "95 miles east — about 1 hour 45 minutes from Columbus",
    zipCodes: ["45202", "45206", "45208", "45209", "45211", "45213", "45220", "45226", "45227", "45236"],
    neighborhoods: [
      "Downtown Cincinnati",
      "Over-the-Rhine",
      "Hyde Park",
      "Oakley",
      "Mount Lookout",
      "Clifton",
      "Anderson Township",
    ],
    landmarks: [
      "Great American Ball Park",
      "Findlay Market",
      "University of Cincinnati",
      "Smale Riverfront Park",
      "Cincinnati Museum Center",
    ],
    intro:
      "Cincinnati is a natural eastern extension of our service area from Columbus, especially for customers relocating between south-central Indiana and the Ohio border. We handle Cincinnati moves for families, professionals, and businesses that want a crew comfortable with interstate logistics, urban loading constraints, and longer-distance planning without losing the local-service feel.",
    highlights: [
      "Regular Indiana-to-Ohio moving coverage",
      "Experienced with downtown, condo, and neighborhood relocations",
      "Straightforward pricing for Columbus-to-Cincinnati routes",
      "Most popular for: cross-state moves, career relocations, apartment transitions",
    ],
    localNote:
      "Cincinnati moves often involve tighter urban streets, multi-unit buildings, and more scheduling coordination than smaller Indiana towns. We plan truck access and timing in advance so move day stays smooth whether you're headed downtown, to the east side, or into a nearby suburb.",
    testimonial: {
      name: "Jordan H.",
      neighborhood: "Oakley, Cincinnati",
      text:
        "Swift helped us relocate from Columbus to Cincinnati and made the whole day feel easy. Communication was great, pricing was clear, and nothing arrived damaged.",
    },
    faq: [
      {
        q: "Do you move customers between Columbus and Cincinnati?",
        a: "Yes. Columbus-to-Cincinnati and Cincinnati-to-Columbus moves are both within our regular cross-state coverage.",
      },
      {
        q: "Can you handle Indiana-to-Ohio interstate moves?",
        a: "Yes — we are equipped for interstate moving and regularly serve routes connecting southeast Indiana and the Cincinnati metro.",
      },
      {
        q: "Do you serve nearby Cincinnati suburbs too?",
        a: "Yes. We can often help in surrounding areas near Cincinnati depending on the exact address and schedule.",
      },
    ],
  },
];

// ─── Service focus classification ─────────────────────────────────────────
// Cities within 30 minutes of Columbus HQ get the full service menu
// (moving + cleaning). Everything else targets moving-only.
const MOVING_AND_CLEANING_SLUGS = new Set([
  "columbus",
  "edinburgh",
  "seymour",
  "north-vernon",
  "hope",
  "taylorsville",
  "elizabethtown",
]);

export const getServiceFocus = (slug) =>
  MOVING_AND_CLEANING_SLUGS.has(slug) ? "moving_cleaning" : "moving_only";

export const isFullServiceLocation = (slug) =>
  MOVING_AND_CLEANING_SLUGS.has(slug);

export const getLocationBySlug = (slug) =>
  LOCATIONS.find((l) => l.slug === slug);

// Kept in sync with backend/routes/seo.py SERVICE_AREAS
export const LOCATION_SLUGS = LOCATIONS.map((l) => l.slug);
